/**
 * `/oauth` — where every OAuth provider sends the popup back (the static redirect_uri registered
 * with the provider). Two callers share it:
 *
 *  - the webapp's own account-management flow: the opener listens on `BroadcastChannel('oauth_channel')`
 *    and completes the flow itself — same-origin, and the only relay that survives a provider's
 *    COOP swap (which nulls `window.opener` for the rest of the popup's life);
 *  - another first-party origin (Lookout) that appended a `state` naming itself: this page
 *    `location.replace()`s to that origin's `/oauth/rudderstack`, carrying the same search, so ITS
 *    same-origin listener can complete the flow. Only an origin in the build-time allowlist
 *    (`REACT_APP_OAUTH_RETURN_ORIGINS`) is ever navigated to — see `oauthReturn.ts`.
 *
 * Nothing here calls an API or reads a credential; the code in the query is only useful to whoever
 * holds the original create payload and a session for the workspace.
 */
import React, { lazy, Suspense, useEffect } from 'react';

import { OAUTH_RETURN_ORIGINS } from '../../config';
import { OAUTH_CHANNEL_NAME, OAUTH_POST_MESSAGE_TYPE } from '../../constants';
import { matchReturnOrigin } from './oauthReturn';

// Same chunk as the router's copy: a static import here would pin WaitingAuthentication (and the
// source/destination icon registries it pulls in) into `main.*.js` and blow the size-limit budget.
const WaitingAuthentication = lazy(
  () => import(/* webpackChunkName: "auth.WaitingAuthentication" */ './WaitingAuthentication'),
);

type OauthRedirectProps = {
  /** Injected for tests; production reads the build-time allowlist. */
  returnOrigins?: readonly string[];
};

export const OauthRedirect: React.FC<OauthRedirectProps> = ({
  returnOrigins = OAUTH_RETURN_ORIGINS,
}) => {
  useEffect(() => {
    const { search, origin } = window.location;
    const params = new URLSearchParams(search);
    const code = params.get('code');
    const error = params.get('error');

    const returnTo = matchReturnOrigin(params.get('state'), returnOrigins, origin);
    if (returnTo) {
      window.location.replace(`${returnTo}/oauth/rudderstack${search}`);
      return undefined;
    }

    const channel = new window.BroadcastChannel(OAUTH_CHANNEL_NAME);
    channel.postMessage({ type: OAUTH_POST_MESSAGE_TYPE, payload: { code, search, error } });
    channel.close();

    return () => {
      channel.close();
    };
  }, [returnOrigins]);

  return (
    <Suspense fallback={null}>
      <WaitingAuthentication />
    </Suspense>
  );
};
